import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { DefaultStatus } from '@bciti/types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, from } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { Service } from 'src/app/global/services/service-state/service-state.model';
import {
    DeleteServiceAction,
    DeleteServiceErrorAction,
    DeleteServiceSuccessAction,
    DuplicateServiceAction,
    GetServicesAction,
    GetServicesErrorAction,
    GetServicesSuccessAction,
    UpdateServiceAction,
    UpdateServiceErrorAction,
    UpdateServiceOrderAction,
} from 'src/app/global/store/global.actions';
import { servicesSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { MatPaginator } from '@angular/material/paginator';
import { ActionMenuOption } from '../../common/table/bciti-table.component';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { ServiceManualStatusChangeDialogComponent } from '../../dialogs/service-manual-status-change-dialog/service-manual-status-change-dialog.component';
import { FormCheckboxComponent } from '../../forms/checkbox/checkbox.component';

export interface ActionMenu {
    options: ActionMenuOption[];
}

@Component({
    selector: 'app-service-state-table',
    templateUrl: './service-state-table.component.html',
    styleUrls: ['./service-state-table.component.scss'],
})
export class ServiceStateTableComponent implements OnInit, AfterViewInit, OnDestroy {
    destroy$ = new Subject();

    DefaultStatus = DefaultStatus;

    loading = false;

    columnKeys = ['name', 'category', 'starred', 'schedule', 'action'];

    actionMenuOptions = [];

    get currentLang(): string {
        return this.translate.currentLang;
    }

    @ViewChild('serviceTable') serviceTable: MatTable<any>;

    @ViewChildren('starredCheckbox') starredCheckboxes: QueryList<FormCheckboxComponent>;

    @ViewChild(MatPaginator) paginator: MatPaginator;

    dataSource = new MatTableDataSource<Service>();

    constructor(
        private translate: TranslateService,
        private dialog: MatDialog,
        private store: Store<State>,
        private actions$: Actions,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.initTranslatedObjects();

        this.store
            .select(servicesSelector)
            .pipe(
                takeUntil(this.destroy$),
                map((services) => {
                    this.dataSource.data = services;
                }),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(GetServicesAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(GetServicesSuccessAction, GetServicesErrorAction),
                tap(() => (this.loading = false)),
            )
            .subscribe();

        this.translate.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.store.dispatch(GetServicesAction());
                    this.initTranslatedObjects();
                    this.paginator._intl.itemsPerPageLabel = this.translate.instant('common.paginator.itemsPerPage');
                }),
            )
            .subscribe();

        this.store
            .select(servicesSelector)
            .pipe(
                take(1),
                filter((services) => services.length === 0),
                tap(() => this.store.dispatch(GetServicesAction())),
            )
            .subscribe();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
    }

    initTranslatedObjects(): void {
        this.actionMenuOptions = [
            {
                label: this.translate.instant('pages.services-state.list.table.action-menu.edit'),
                eventEmitterTag: 'edit',
            },
            {
                label: this.translate.instant('pages.services-state.list.table.action-menu.duplicate'),
                eventEmitterTag: 'duplicate',
            },
            {
                label: this.translate.instant('pages.services-state.list.table.action-menu.delete'),
                eventEmitterTag: 'delete',
            },
        ];
    }

    drop(event: CdkDragDrop<string[]>) {
        // Consider paginator's current pageIndex and pageSize when determining the service's new 'order'
        // The paginator's pageIndex is zero-indexed
        const fromIndex = event.previousIndex + this.paginator.pageSize * this.paginator.pageIndex;
        const toIndex = event.currentIndex + this.paginator.pageSize * this.paginator.pageIndex;

        if (fromIndex === toIndex) {
            return;
        }

        const movedItem = this.dataSource.data[fromIndex];
        const remainingItems = this.dataSource.data.filter((_, index) => index !== fromIndex);

        // Do not directly modify dataSource. Modify new data object to avoid affecting UI as we manipulate the array.
        const newData = [];

        remainingItems.splice(toIndex, 0, movedItem);
        remainingItems.map((item, index) => ({
            ...item,
            order: index + 1,
        }));

        remainingItems.forEach((item) => {
            newData.push(item);
        });

        // Assign newData to dataSource which should match the incoming UpdateServiceOrder response which will overwrite this data
        this.dataSource.data = newData;

        // Table is 0 indexed, but the order submitted must be 1 indexed
        // so we add 1 to 'toIndex'
        const movedService = {
            ...movedItem,
            order: toIndex + 1,
        };

        this.store.dispatch(UpdateServiceOrderAction({ service: movedService }));

        this.serviceTable.renderRows();
    }

    getBackgroundPreviewDropColor(index: number): string {
        if (index % 2 === 0) return 'cdk-drag-preview-color';
    }

    handleClick(tag: string, element: Service) {
        switch (tag) {
            case 'override-end':
                return this.changeClosedServiceStatusToRegular(element);
            case 'open-status':
                return this.openManualStatusChangeDialog(element, tag);
            case 'close-status':
                return this.openManualStatusChangeDialog(element, tag);
            case 'edit':
                this.router.navigate([`services/edit/${element._id}`]);
                return;
            case 'duplicate':
                this.handleDuplicate(element);
                return;
            case 'delete':
                return this.handleDelete(element);
            default:
                console.log('unknown tag', tag);
        }
    }

    handleStarred(e: Event, element: Service): void {
        this.store.dispatch(UpdateServiceAction({ service: { _id: element._id, starred: (e.target as HTMLInputElement).checked } }));

        this.actions$
            .pipe(
                ofType(UpdateServiceErrorAction),
                take(1),
                tap(() => {
                    const errorCheckbox = this.starredCheckboxes.find((checkbox) => checkbox.id === (e.target as HTMLElement).id);
                    errorCheckbox.value = !errorCheckbox.value;
                }),
            )
            .subscribe();
    }

    handleDuplicate(service: Service) {
        this.store.dispatch(DuplicateServiceAction({ service }));
    }

    handleDelete(service: Service): void {
        // open confirm dialog
        const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translate.instant('pages.services-state.dialogs.delete-service.title'),
                body: this.translate.instant('pages.services-state.dialogs.delete-service.body', { name: service.name[this.currentLang] }),
                buttons: {
                    confirm: {
                        text: this.translate.instant('common.delete'),
                        class: 'bciti-button bciti-button--red',
                    },
                    cancel: {
                        text: this.translate.instant('common.cancel'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        // handle user confirm
        confirmDialogRef.componentInstance.onSubmit.subscribe((confirm) => {
            if (confirm) {
                confirmDialogRef.componentInstance.loading = true;
                this.store.dispatch(DeleteServiceAction({ service }));

                this.actions$
                    .pipe(
                        ofType(DeleteServiceSuccessAction, DeleteServiceErrorAction),
                        take(1),
                        tap(() => (confirmDialogRef.componentInstance.loading = false)),
                        ofType(DeleteServiceSuccessAction),
                        tap(() => confirmDialogRef.close()),
                    )
                    .subscribe();
            } else {
                confirmDialogRef.close();
            }
        });
    }

    changeClosedServiceStatusToRegular(element: Service) {
        const serviceNoOverride = {
            ...element,
            override: null,
        };

        this.store.dispatch(UpdateServiceAction({ service: serviceNoOverride }));
    }

    openManualStatusChangeDialog(element: Service, tag: string) {
        this.dialog.open(ServiceManualStatusChangeDialogComponent, {
            data: { service: element, tag },
            panelClass: 'manual-status-change-dialog',
            autoFocus: false,
            width: '800px',
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
